import React, { useState } from 'react';
import { range } from 'lodash-es';
import { Box, Divider, FormField, FormHelp, Input, Label, Text, TextButton } from '@zillow/constellation-10';
import { IconPlusFilled } from '@zillow/constellation-icons';

import type { Section } from '../../../models/index';
import { Field, NonRefundableFeesSection } from '../../../models/index';
import { NON_REFUNDABLE_FEES } from '../../../constants/index';
import { FEE_PAYMENT_TIME, MAX_AMOUNT_ERROR_MESSAGE } from '../../../constants/stringConstants';
import YesAndNo from '../../Widgets/YesAndNo';
import CurrencyInput from '../../Widgets/CurrencyInput';
import FeePaymentTime from '../../Widgets/FeePaymentTime';
import { isYesNoEnabledForNumberedFields } from '../../../constants/functionConstants';
import { useIndexedSection } from '../../../hooks';

export const MAXIMUM_FEES = 5;

type Props = {
  section: NonRefundableFeesSection;
  setSectionValues: (object) => void;
  setSection: (section: NonRefundableFeesSection) => void;
};

export default function NonRefundableFees_v3({ section, setSectionValues, setSection }: Props) {
  const [hasClickedNo, setHasClickedNo] = useState(false);

  function setCustomAmountValidation(section: Section, index: number): void {
    const fieldName = Field.getIndexedFieldName(NON_REFUNDABLE_FEES.FIELD_NAME.AMOUNT, Number(index));
    section.setCustomFieldValidator(fieldName, isFeeAmountValid.bind(this, fieldName));
  }

  const { onAddOne, onDeleteOne } = useIndexedSection({
    section,
    setSection,
    SectionClass: NonRefundableFeesSection,
    onIndexAdded: setCustomAmountValidation,
  });

  function isFeeAmountValid(feeFieldName: string, amount: string): boolean {
    if (Number(amount.replace(/,/g, '')) > NON_REFUNDABLE_FEES.BOUNDS.MAX_FEE_AMOUNT) {
      section
        ?.getField(feeFieldName)
        ?.setErrorMessage(MAX_AMOUNT_ERROR_MESSAGE(NON_REFUNDABLE_FEES.BOUNDS.MAX_FEE_AMOUNT));
      return false;
    }
    return true;
  }

  function onEnableNonRefundableFees(): void {
    const updatedSection = section.clone();
    updatedSection.setFieldValue(NON_REFUNDABLE_FEES.FIELD_NAME.IS_ENABLED, true);
    updatedSection.addOneIndex();

    const index = updatedSection.getTotalIndexes() - 1;
    setCustomAmountValidation(updatedSection, index);

    setSection(updatedSection);
  }

  function onRemoveAll(): void {
    setHasClickedNo(true);
    const updatedSection = section.clone();
    updatedSection.removeAllIndexes();
    updatedSection.setFieldValue(NON_REFUNDABLE_FEES.FIELD_NAME.IS_ENABLED, false);
    updatedSection.setFieldValue(NON_REFUNDABLE_FEES.FIELD_NAME.FIRST_PAY_DUE_SIGN, false);
    updatedSection.setFieldValue(NON_REFUNDABLE_FEES.FIELD_NAME.FIRST_PAY_DUE_START, false);
    setSection(updatedSection);
  }

  function setAmount(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { index } = e.currentTarget.dataset;
    const fieldName = Field.getIndexedFieldName(NON_REFUNDABLE_FEES.FIELD_NAME.AMOUNT, Number(index));

    setSectionValues({ [fieldName]: e.currentTarget.value });
  }

  function setName(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { index } = e.currentTarget.dataset;
    const fieldName = Field.getIndexedFieldName(NON_REFUNDABLE_FEES.FIELD_NAME.NAME, Number(index));

    setSectionValues({ [fieldName]: e.currentTarget.value });
  }

  function onPayDueClick(event: React.ChangeEvent<HTMLInputElement>): void {
    const { id } = event.currentTarget;

    setSectionValues({
      [NON_REFUNDABLE_FEES.FIELD_NAME.FIRST_PAY_DUE_SIGN]: id === FEE_PAYMENT_TIME.FIRST_PAY_ON_SIGN,
      [NON_REFUNDABLE_FEES.FIELD_NAME.FIRST_PAY_DUE_START]: id === FEE_PAYMENT_TIME.FIRST_PAY_ON_LEASE_START,
    });
  }

  function renderFee(index: number): React.ReactNode {
    const nameField = section.getField(Field.getIndexedFieldName(NON_REFUNDABLE_FEES.FIELD_NAME.NAME, index));
    const amountField = section.getField(Field.getIndexedFieldName(NON_REFUNDABLE_FEES.FIELD_NAME.AMOUNT, index));

    return (
      <Box>
        {index > 0 && (
          <Box
            css={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr',
              paddingTop: 'layout.tighter',
              paddingBottom: 'layout.tighter',
            }}
          >
            <Box css={{ display: 'flex', alignItems: 'center', height: '1rem' }}>
              <Text textStyle="body-bold">Non-refundable fee #{index + 1}</Text>
            </Box>
            <Box css={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', height: '1rem' }}>
              <TextButton data-index={index} data-test="delete-fee" onClick={onDeleteOne}>
                Delete
              </TextButton>
            </Box>
          </Box>
        )}
        <FormField
          required
          css={{ marginBottom: 'layout.tighter' }}
          label={<Label id={`name-${index}`}>{NON_REFUNDABLE_FEES.LABEL.NAME}</Label>}
          control={
            <Input
              aria-labelledby={`name-${index}`}
              data-index={index}
              data-test="name"
              value={nameField.enteredValue}
              onChange={setName}
            />
          }
        />
        <FormField
          required
          error={amountField.hasError}
          label={<Label id={`amount-${index}`}>{NON_REFUNDABLE_FEES.LABEL.AMOUNT}</Label>}
          control={
            <CurrencyInput
              aria-labelledby={`amount-${index}`}
              data-index={index}
              data-test="amount"
              value={amountField.enteredValue}
              onChange={setAmount}
              onBlur={setAmount}
              error={amountField.hasError}
            />
          }
          description={amountField.hasError && <FormHelp>{amountField.getErrorMessage()}</FormHelp>}
        />
      </Box>
    );
  }

  const totalNonRefundableFees = section.getTotalIndexes();
  const isEnabled = isYesNoEnabledForNumberedFields(section, 'nonRefundableFees0_isEnabled', hasClickedNo);

  if (totalNonRefundableFees == null) {
    return null;
  }

  return (
    <>
      <FormField
        required
        css={{ marginBottom: totalNonRefundableFees > 0 ? 'sm' : undefined }}
        label={<Label>Will there be non-refundable fees?</Label>}
        control={<YesAndNo yesHandler={onEnableNonRefundableFees} noHandler={onRemoveAll} yesActive={isEnabled} />}
        description={
          <FormHelp>
            Examples include administration and move-in fees that are collected at signing. The total of these fees
            should be less than one month’s rent. Do not include pet fees, which are listed elsewhere.
          </FormHelp>
        }
      />
      {totalNonRefundableFees > 0 && (
        <>
          <Box>
            <FeePaymentTime
              onChange={onPayDueClick}
              firstChecked={section.getFieldValue(NON_REFUNDABLE_FEES.FIELD_NAME.FIRST_PAY_DUE_SIGN)}
              secondChecked={section.getFieldValue(NON_REFUNDABLE_FEES.FIELD_NAME.FIRST_PAY_DUE_START)}
              section={section}
              label="When are the additional non-refundable fees due?"
            />
          </Box>
          <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />
        </>
      )}
      {range(0, totalNonRefundableFees).map((index: number) => (
        <React.Fragment key={index}>
          {index > 0 && <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />}
          {renderFee(index)}
        </React.Fragment>
      ))}
      {totalNonRefundableFees < MAXIMUM_FEES ? (
        totalNonRefundableFees > 0 && (
          <TextButton
            css={{ marginTop: 'layout.tight' }}
            data-test="add-another"
            icon={<IconPlusFilled />}
            onClick={onAddOne}
          >
            Add another non-refundable fee
          </TextButton>
        )
      ) : (
        <Text>
          <span data-test="maximum-reached">
            You’ve added {MAXIMUM_FEES} non-refundable fees, which is the maximum allowed.
          </span>
        </Text>
      )}
    </>
  );
}

NonRefundableFees_v3.description = 'Choose when NRF is due';
